import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Copy, Eye, EyeOff, KeyRound, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import { formatAccessCode } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, ErrorState, ListSkeleton, Refreshing } from "@/components/admin/AsyncView";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { fmtLong } from "@/components/admin/format";

// Akses storage: kode disembunyikan default, identitas dulu, aksi
// pencabutan/pembuatan ulang selalu lewat konfirmasi yang jelas.
export default function AccessPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [copied, setCopied] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type:'revoke'|'regen', cred}

  const load = async (background = false) => {
    if (background) setRefreshing(true);
    else { setLoading(true); setError(null); }
    try {
      const { data } = await api.get("/admin/access-credentials");
      setRows(data);
      setError(null);
    } catch (e) {
      if (!background) setError(errMsg(e));
      else toast.error(`Tidak dapat memperbarui: ${errMsg(e)}. Menampilkan data terakhir.`);
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || []).filter((c) => !needle
      || c.user_name?.toLowerCase().includes(needle)
      || c.booking_code?.toLowerCase().includes(needle));
  }, [rows, q]);

  const copyCode = async (cred) => {
    try {
      await navigator.clipboard.writeText(cred.code);
      setCopied(cred.id);
      toast.success("Kode disalin");
      setTimeout(() => setCopied((v) => (v === cred.id ? null : v)), 2000);
    } catch {
      toast.error("Gagal menyalin kode");
    }
  };

  const runAccess = async (type, cred) => {
    setBusyId(cred.id);
    try {
      if (type === "revoke") {
        await api.post(`/admin/bookings/${cred.booking_id}/disable-access`);
        toast.success("Akses dimatikan");
      } else {
        await api.post(`/admin/bookings/${cred.booking_id}/regenerate-access`);
        toast.success("Kode baru dibuat, kode lama tidak berlaku");
      }
      await load(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <AccessHeader total={null} />
        <ListSkeleton rows={4} />
      </div>
    );
  }
  if (error && rows === null) {
    return (
      <div className="space-y-5">
        <AccessHeader total={null} />
        <ErrorState message={error} onRetry={() => load()} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AccessHeader total={rows.length} refreshing={refreshing} onRefresh={() => load(true)} />

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex h-10 min-w-0 basis-full items-center gap-2 rounded-lg border border-white/10 bg-zinc-900 px-3 sm:basis-auto sm:flex-1 sm:max-w-xs">
          <span className="sr-only">Cari nama atau kode booking</span>
          <Input data-testid="access-search" value={q}
            onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {}, { replace: true })}
            placeholder="Cari nama atau kode booking" className="h-full border-0 bg-transparent p-0 text-sm placeholder:text-zinc-600 focus-visible:ring-0" />
        </label>
        <span className="ml-auto text-xs tabular-nums text-zinc-500">{filtered.length} dari {rows.length} akses aktif</span>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          kind={q ? "filtered" : "zero"}
          title={q ? "Tidak ada hasil" : "Tidak ada akses aktif"}
          hint={q ? "Coba kata kunci lain." : "Kode akses aktif muncul setelah booking disetujui."}
        />
      ) : (
        <ol data-testid="active-access-list" className="divide-y divide-white/5 overflow-hidden rounded-2xl border border-white/10 bg-zinc-900">
          {filtered.map((c) => {
            const show = !!revealed[c.id];
            const busy = busyId === c.id;
            return (
              <li key={c.id} data-testid={`access-row-${c.booking_code}`} className="px-4 py-3.5">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
                  <div className="min-w-0">
                    <p className="break-words text-sm font-semibold text-zinc-100">{c.user_name || "-"}</p>
                    <p className="font-mono text-xs text-zinc-500">
                      Booking {c.booking_code || "-"}{c.simulated ? " · simulasi" : ""}
                    </p>
                    <p className="mt-1 text-xs text-zinc-400">
                      Berlaku {fmtLong(c.valid_from)} - {fmtLong(c.valid_until)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 rounded-xl bg-zinc-950/70 px-3 py-2">
                    {show ? (
                      <span data-testid={`access-code-${c.booking_code}`} className="font-mono text-lg font-bold tracking-widest text-emerald-400">
                        {formatAccessCode(c.code)}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 font-mono text-lg font-bold tracking-widest text-zinc-600" aria-label="Kode disembunyikan">
                        <Lock className="h-3.5 w-3.5" /> •• ••
                      </span>
                    )}
                    <button
                      data-testid={`reveal-access-${c.booking_code}`}
                      onClick={() => setRevealed((r) => ({ ...r, [c.id]: !r[c.id] }))}
                      aria-label={show ? "Sembunyikan kode" : "Tampilkan kode"}
                      aria-pressed={show}
                      className="min-h-[44px] min-w-[44px] rounded-lg p-2 text-zinc-400 hover:bg-white/10 hover:text-white"
                    >
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button
                      data-testid={`copy-access-${c.booking_code}`}
                      onClick={() => copyCode(c)}
                      aria-label="Salin kode"
                      className="min-h-[44px] min-w-[44px] rounded-lg p-2 text-zinc-400 hover:bg-white/10 hover:text-white"
                    >
                      {copied === c.id ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button data-testid={`regenerate-access-${c.booking_code}`} variant="outline" disabled={busy}
                    onClick={() => setConfirm({ type: "regen", cred: c })}
                    className="h-10 rounded-full border-white/15 bg-transparent px-4 text-xs text-zinc-200 hover:bg-white/10">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="mr-1.5 h-3.5 w-3.5" />} Generate ulang
                  </Button>
                  <Button data-testid={`disable-access-${c.booking_code}`} variant="outline" disabled={busy}
                    onClick={() => setConfirm({ type: "revoke", cred: c })}
                    className="h-10 rounded-full border-rose-500/40 bg-transparent px-4 text-xs text-rose-300 hover:bg-rose-500/10">
                    Matikan akses
                  </Button>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.type === "revoke" ? `Matikan akses ${confirm?.cred.user_name}?` : `Buat kode baru untuk ${confirm?.cred.user_name}?`}
        description={confirm?.type === "revoke"
          ? `Booking ${confirm?.cred.booking_code}: kode saat ini langsung tidak berlaku. Peminjam tidak bisa membuka storage.`
          : `Booking ${confirm?.cred.booking_code}: kode lama langsung tidak berlaku dan diganti kode baru.`}
        confirmLabel={confirm?.type === "revoke" ? "Matikan akses" : "Buat kode baru"}
        danger={confirm?.type === "revoke"}
        testId="access-confirm"
        pending={confirm && busyId === confirm.cred.id}
        onConfirm={() => confirm && runAccess(confirm.type, confirm.cred)}
      />
    </div>
  );
}

function AccessHeader({ total, refreshing, onRefresh }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h1 className="text-2xl font-semibold tracking-tight text-white">Akses storage</h1>
      <p className="text-sm text-zinc-400">
        {total == null ? "Memuat akses aktif…" : total === 0 ? "Tidak ada akses aktif." : `${total} akses aktif.`}
      </p>
      {refreshing ? <Refreshing /> : onRefresh && (
        <button onClick={onRefresh} className="ml-auto text-xs text-zinc-500 underline-offset-4 hover:text-zinc-300 hover:underline">Muat ulang</button>
      )}
    </div>
  );
}
